// "Try the demo": start a restricted session on the shared demo accounts without signing up.
//
// The token comes from the backend (POST /auth/demo/login) rather than the browser logging in
// with a password, so no demo credential ever reaches the bundle. The token carries a `demo`
// claim which the server uses to block destructive and expensive actions.
//
// isDemoToken is PRESENTATION ONLY - it decides whether to show the demo banner. The server is
// the authority on what a demo session may actually do; never gate data on this.

import { API_BASE } from "./apiBase";
import { getTokenPayload } from "./session";

export function isDemoToken(token) {
  const payload = getTokenPayload(token);
  // Identity check, matching the server: the string "true" must not pass.
  return !!payload && payload.demo === true;
}

export class DemoUnavailableError extends Error {}
export class DemoBusyError extends Error {}

// Returns an access token. Throws DemoBusyError on the rate limit (so the UI can say "try again
// in a moment") and DemoUnavailableError for everything else.
//
// Pass the CURRENT demo token as `existingToken` when switching roles. The backend reads the
// session id off it and hands back the other half of this visitor's own account pair, instead of
// provisioning a whole new session - which is what makes "send it as the owner, answer it as the
// shop" work on the same conversation.
export async function startDemoSession(role, existingToken = null) {
  const headers = { "Content-Type": "application/json" };
  if (existingToken && isDemoToken(existingToken)) {
    headers.Authorization = `Bearer ${existingToken}`;
  }

  let res;
  try {
    res = await fetch(`${API_BASE}/auth/demo/login`, {
      method: "POST",
      headers,
      body: JSON.stringify({ role }),
    });
  } catch {
    throw new DemoUnavailableError("Could not reach the demo.");
  }

  if (res.status === 429) {
    throw new DemoBusyError("The demo is busy right now - please try again in a moment.");
  }
  if (!res.ok) {
    throw new DemoUnavailableError("The demo is unavailable right now - please try again later.");
  }

  const data = await res.json().catch(() => null);
  if (!data || !data.access_token) {
    throw new DemoUnavailableError("The demo is unavailable right now - please try again later.");
  }
  return data.access_token;
}
